import {Component, Inject, OnInit} from '@angular/core';
import {DateAdapter} from '@angular/material/core';
import {MatDialog} from '@angular/material/dialog';
import {DOCUMENT} from '@angular/common';
import {Event as RouterEvent, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {Moment} from 'moment';
import {merge} from 'rxjs';
import {BusyIndicatorService} from './components/busy-indicator/busy-indicator.service';
import {UserDependentComponent} from './components/user-dependent-component';
import {BusinessDataService} from './data-repositories/business-data.service';
import {CountryService} from './data-repositories/country-service';
import {LanguageService} from './data-repositories/language.service';
import {UserStore} from './models/user-store';
import {AuthService} from './repositories/auth.service';
import {BrowserStorageService} from './services/browser-storage.service';
import {GoogleTagManagerService} from './services/google-tag-manager.service';
import {JointInfoComponent} from './pages/workspace/stages/components/joint-info/joint-info.component';
import {ExitConfirmComponent} from './components/exit-confirm/exit-confirm.component';

@Component({
  standalone: false,
  selector: '[app-root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent extends UserDependentComponent {
  title = 'iba-app';
  isNavigating = false;

  constructor(
    translate: TranslateService,
    service: BusinessDataService,
    storageService: BrowserStorageService,
    authService: AuthService,
    countryService: CountryService,
    @Inject(DOCUMENT) private document: Document,
    dateAdapter: DateAdapter<Moment>,
    public languageService: LanguageService,
    gtmService: GoogleTagManagerService,
    private dialog: MatDialog,
    private busyService: BusyIndicatorService,
    private router: Router) {

    super(service, authService);

    this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationStart) {
        this.isNavigating = true;
      } else if (event instanceof NavigationEnd
        || event instanceof NavigationCancel
        || event instanceof NavigationError) {
        this.isNavigating = false;
      }
    });

    const initialUrl = storageService.getSession('initialUrl');
    if (!initialUrl) {
      storageService.setSession('initialUrl', window.location.href);
    }

    gtmService.init();

    let lang = '';
    if (storageService.getLocal('lang')) {
      lang = storageService.getLocal('lang');
      dateAdapter.setLocale(lang);
    }

    translate.use(lang);
    merge(authService.isUserLoggedIn, storageService.onLocalItemChanged)
      .subscribe(() => {
        const currentLang = storageService.getLocal('lang');
        if (authService.isLoggedIn()) {
          busyService.beginProcess();
          countryService.refreshCountryList(() => busyService.endProcess());

          busyService.beginProcess();
          languageService.refreshInfoFields(currentLang, () => busyService.endProcess());

          busyService.beginProcess();
          languageService.refresQuestions(currentLang, () => busyService.endProcess());

          busyService.beginProcess();
          languageService.refresAgreements(currentLang, () => busyService.endProcess());

          dateAdapter.setLocale(currentLang);
        }

        languageService.setDirection('rtl');
        this.loadBootstrap('rtl');
      });

    if (authService.isLoggedIn()) {
      service.getUser(authService.phone, (user) => {
        service.SetUser({model: user, initial: true} as UserStore);
        authService.setUserStatus(user, authService.isAdmin);
      });
    } else {
      const subscribe = authService.isUserLoggedIn.subscribe(isLogged => {
        if (isLogged) {
          busyService.beginProcess();
          service.getUser(authService.phone, (user) => {
            service.SetUser({model: user, initial: true} as UserStore);
            authService.setUserStatus(user, authService.isAdmin);
            subscribe.unsubscribe();
            busyService.endProcess();
          });
        }

      });
    }
  }

  exit() {
    if (this.router.url.startsWith('/documentview')) {
      window.close();
      return;
    }

    const dialogRef = this.dialog.open(ExitConfirmComponent, {
      maxWidth: 460,
      width: '80%',
      panelClass: ['dialog-container', 'info-panel', 'p-0'],
      backdropClass: 'info-backdrop',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(d => {
      if (!!d) {
        location.replace('https://www.inter-il.com');
      }
    });
  }

  loadBootstrap(direction: 'rtl' | 'ltr') {
    const head = this.document.getElementsByTagName('head')[0];
    const bootstrapLink = this.document.getElementById('bootstrap') as HTMLLinkElement;

    let styleHref = '';
    switch (direction) {
      case 'ltr':
        styleHref = 'bootstrap.css';
        break;
      case 'rtl':
        styleHref = 'bootstrap-rtl.css';
        break;
    }

    if (bootstrapLink) {
      bootstrapLink.href = styleHref;
    } else {
      const style = this.document.createElement('link');
      style.id = 'bootstrap';
      style.rel = 'stylesheet';
      style.href = styleHref;

      head.appendChild(style);
    }
  }
}
